import {
    ApiChatChannelType,
    ApiChatMessageKind,
    ApiChatMessageSendStatus,
} from '@/core/generated/api';
import type { ChatDialogMessageResponse } from '@/core/generated/api';

const ECHO_INTERVAL_MS = 15_000;

/** Сравнивает содержимое вложений без идентификаторов записей хранилища */
const haveSameAttachments = (
    left: ChatDialogMessageResponse,
    right: ChatDialogMessageResponse
): boolean => {
    const first = [...(left.attachments ?? [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    const second = [...(right.attachments ?? [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

    return first.length === second.length && first.every((attachment, index) => {
        const other = second[index];

        return Boolean(attachment.url) && attachment.url === other.url
            && attachment.kind?.id === other.kind?.id;
    });
};

/** Определяет пару ответа оператора и исходящего события подключённого аккаунта */
const isEchoPair = (
    operator: ChatDialogMessageResponse,
    echo: ChatDialogMessageResponse
): boolean => {
    const hasOperator = Boolean(operator.authorParticipantId && operator.authorName && operator.clientMessageId);
    const hasEcho = !echo.authorParticipantId && !echo.authorName && !echo.clientMessageId;

    const sameAccount = Boolean(operator.senderParticipantId)
        && operator.senderParticipantId === echo.senderParticipantId;

    const confirmed = operator.sendStatus?.id === ApiChatMessageSendStatus.Accepted
        && echo.sendStatus?.id === ApiChatMessageSendStatus.Accepted;

    const regular = operator.kind?.id === ApiChatMessageKind.Regular
        && echo.kind?.id === ApiChatMessageKind.Regular;

    const hasContent = Boolean(operator.text) || (operator.attachments?.length ?? 0) > 0;
    const sentAt = Date.parse(operator.sentAt || operator.receivedAt || '');
    const echoedAt = Date.parse(echo.sentAt || echo.receivedAt || '');

    const closeInTime = Number.isFinite(sentAt) && Number.isFinite(echoedAt)
        && Math.abs(echoedAt - sentAt) <= ECHO_INTERVAL_MS;

    return Boolean(operator.id && echo.id && operator.id !== echo.id
        && operator.isOutgoing && echo.isOutgoing && hasOperator && hasEcho && sameAccount
        && confirmed && regular && hasContent && closeInTime
        && (operator.text ?? '') === (echo.text ?? '') && haveSameAttachments(operator, echo));
};

/** Показывает однозначную соседнюю пару ответа и эха Salebot одной карточкой оператора */
export const getChatDialogDisplayMessages = (
    messages: readonly ChatDialogMessageResponse[],
    channel: ApiChatChannelType | undefined,
): readonly ChatDialogMessageResponse[] => {
    if (!channel || channel === ApiChatChannelType.Vox) return messages;

    const result: ChatDialogMessageResponse[] = [];

    for (let index = 0; index < messages.length; index += 1) {
        const current = messages[index];
        const next = messages[index + 1];

        if (!next) {
            result.push(current);

            continue;
        }

        const operator = current.authorParticipantId
            ? current
            : next;

        const echo = operator === current
            ? next
            : current;

        const adjacent = Number.isSafeInteger(current.sequence) && Number.isSafeInteger(next.sequence)
            && (next.sequence ?? 0) === (current.sequence ?? 0) + 1;

        if (adjacent && isEchoPair(operator, echo)) {
            const ambiguous = messages.some((other) => other !== operator && other !== echo
                && (isEchoPair(operator, other) || isEchoPair(other, echo)));

            if (!ambiguous) {
                result.push(operator);
                index += 1;

                continue;
            }
        }

        result.push(current);
    }

    return result;
};
